import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import GroveLaunchTermsModal from "./GroveLaunchTermsModal";

const schema = z.object({
  full_name: z.string().trim().min(1, "Required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().regex(/^[+\d\s\-()]{8,20}$/, "Invalid phone"),
  psn_id: z.string().trim().min(3, "Min 3 chars").max(32),
  owns_gta6: z.literal(true, { errorMap: () => ({ message: "Required" }) }),
  accepted_terms: z.literal(true, { errorMap: () => ({ message: "Required" }) }),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initialFull?: boolean;
}

export default function GroveLaunchWaitlistModal({ open, onOpenChange, initialFull }: Props) {
  const qc = useQueryClient();
  const [success, setSuccess] = useState(false);
  const [isFull, setIsFull] = useState(!!initialFull);
  const [showTerms, setShowTerms] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: { owns_gta6: false as any, accepted_terms: false as any },
  });

  const join = useMutation({
    mutationFn: async (values: FormValues) => {
      const { data, error } = await supabase.functions.invoke("grove-launch-join", { body: values });
      if (error) throw error;
      return data as { ok?: boolean; full?: boolean; duplicate?: "email" | "psn" | "unknown" | boolean; error?: string };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["grove-launch-count"] });
      if (data?.full) { setIsFull(true); return; }
      if (data?.duplicate) {
        if (data.duplicate === "psn") toast.error("This PSN ID is already on the waitlist.");
        else if (data.duplicate === "email") toast.error("This email is already on the waitlist.");
        else toast.error("This entry is already on the waitlist.");
        return;
      }
      if (data?.error) { toast.error(data.error); return; }
      setSuccess(true);
    },
    onError: (e: any) => toast.error(e?.message || "Something went wrong"),
  });

  const handleClose = (v: boolean) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => { reset(); setSuccess(false); }, 200);
    }
  };

  const owns = watch("owns_gta6");
  const terms = watch("accepted_terms");

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Grove Launch Offer — Waitlist</DialogTitle>
            <DialogDescription>
              {success
                ? "You're on the list! A confirmation email with your expected credit dates is on the way. 🎮"
                : isFull
                ? "Sorry, all 50 spots are claimed!"
                : "Lock in your spot for 20% off (up to ₹1,200) as PSN credit."}
            </DialogDescription>
          </DialogHeader>

          {success ? (
            <div className="space-y-4 pt-2">
              <div className="rounded-xl border border-primary/40 bg-primary/10 p-4 text-center text-sm">
                Check your inbox for your confirmation and expected PSN credit date (by Jun 28, 2026).
              </div>
              <Button className="w-full" onClick={() => handleClose(false)}>Done</Button>
            </div>
          ) : isFull ? (
            <Button className="w-full" onClick={() => handleClose(false)}>Close</Button>
          ) : (
            <form onSubmit={handleSubmit((v) => join.mutate(v))} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="full_name">Full Name</Label>
                <Input id="full_name" {...register("full_name")} />
                {errors.full_name && <p className="text-xs text-destructive">{errors.full_name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" type="email" {...register("email")} />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone Number</Label>
                <Input id="phone" {...register("phone")} placeholder="+91…" />
                {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="psn_id">PSN ID</Label>
                <Input id="psn_id" {...register("psn_id")} />
                {errors.psn_id && <p className="text-xs text-destructive">{errors.psn_id.message}</p>}
              </div>

              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={!!owns}
                  onCheckedChange={(c) => setValue("owns_gta6", (c === true) as any, { shouldValidate: true })}
                />
                <span>I confirm I own or will buy GTA 6 on PS5</span>
              </label>
              {errors.owns_gta6 && <p className="-mt-2 text-xs text-destructive">{errors.owns_gta6.message as string}</p>}

              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={!!terms}
                  onCheckedChange={(c) => setValue("accepted_terms", (c === true) as any, { shouldValidate: true })}
                />
                <span>
                  I agree to the{" "}
                  <button type="button" onClick={() => setShowTerms(true)} className="text-primary underline">
                    Terms &amp; Conditions
                  </button>
                </span>
              </label>
              {errors.accepted_terms && <p className="-mt-2 text-xs text-destructive">{errors.accepted_terms.message as string}</p>}

              <Button type="submit" className="w-full" disabled={join.isPending}>
                {join.isPending ? "Submitting…" : "Claim My Spot"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <GroveLaunchTermsModal open={showTerms} onOpenChange={setShowTerms} />
    </>
  );
}
